import React, { useCallback, useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { RefreshCw, TrendingUp, Phone, CheckCircle2, XCircle, Coins, Users } from "lucide-react";

function KpiCard({ icon: Icon, label, value, accent = "azur", suffix, sub, testId }) {
  const accents = {
    azur:  { bg: "bg-[#EEF4FB]", fg: "text-[#397DFF]", border: "border-[#D7E5F4]" },
    ambre: { bg: "bg-[#FFF7E6]", fg: "text-[#92400E]", border: "border-[#FDE68A]" },
    green: { bg: "bg-[#D1FAE5]", fg: "text-[#065F46]", border: "border-[#A7F3D0]" },
    red:   { bg: "bg-[#FEE2E2]", fg: "text-[#991B1B]", border: "border-[#FECACA]" },
    blue:  { bg: "bg-[#DBEAFE]", fg: "text-[#1E40AF]", border: "border-[#BFDBFE]" },
  };
  const a = accents[accent] || accents.azur;
  return (
    <div data-testid={testId} className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_8px_24px_rgba(57,125,255,0.05)] p-5 sm:p-6">
      <div className={`inline-flex items-center justify-center w-10 h-10 rounded-xl ${a.bg} ${a.fg} mb-4`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="text-xs uppercase tracking-[0.15em] text-[#939BA0] font-semibold">{label}</div>
      <div className="mt-1 font-display text-3xl sm:text-4xl font-bold text-[#191D1E]">
        {value}{suffix && <span className="text-base text-[#939BA0] ml-1">{suffix}</span>}
      </div>
      {sub && <div className="mt-1.5 text-xs text-[#687175] leading-snug">{sub}</div>}
    </div>
  );
}

export function StatsPanel() {
  const { user } = useAuth();
  const isCommercant = user?.role === "commercant";
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads/stats");
      setStats(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  if (!stats) {
    return <div className="text-sm text-[#939BA0] py-10 text-center">Chargement…</div>;
  }

  return (
    <div data-testid="stats-panel">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#191D1E]">Statistiques</h2>
          <p className="text-sm text-[#939BA0] mt-0.5">Vision en temps réel de votre activité.</p>
        </div>
        <Button variant="outline" onClick={reload} data-testid="stats-refresh" className="rounded-xl border-[#E5E7EB] hover:bg-[#F5F6F9]">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Actualiser
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {isCommercant ? (
          <>
            <KpiCard testId="kpi-clients-inscrits"  icon={Users}        label="Clients inscrits"  value={stats.total}            accent="azur" />
            <KpiCard testId="kpi-clients-souscrits" icon={CheckCircle2} label="Clients souscrits" value={stats.convertis}        accent="green" />
            <KpiCard testId="kpi-cashback-credite"  icon={Coins}        label="Cashback crédité"  value={stats.cashback_engage} suffix="€" accent="ambre" sub="Financé par Place des Énergies" />
            <KpiCard testId="kpi-cashback-restant"  icon={Coins}        label="Cashback restant"  value={stats.cashback_restant} suffix="€" accent="green" sub="Bons d'achat encore à utiliser par vos clients" />
          </>
        ) : (
          <>
            <KpiCard testId="kpi-total"             icon={TrendingUp}   label="Total leads"       value={stats.total}            accent="azur" />
            <KpiCard testId="kpi-a-rappeler"        icon={Phone}        label="À rappeler"        value={stats.a_rappeler}       accent="ambre" />
            <KpiCard testId="kpi-rappeles"          icon={Phone}        label="Rappelés"          value={stats.rappeles}         accent="blue" />
            <KpiCard testId="kpi-convertis"         icon={CheckCircle2} label="Convertis"         value={stats.convertis}        accent="green" />
            <KpiCard testId="kpi-perdus"            icon={XCircle}      label="Perdus"            value={stats.perdus}           accent="red" />
            <KpiCard testId="kpi-cashback-engage"   icon={Coins}        label="Cashback engagé"   value={stats.cashback_engage}  suffix="€" accent="ambre" />
            {user?.role === "admin" && (
              <KpiCard testId="kpi-cashback-restant"  icon={Coins}        label="Cashback restant"  value={stats.cashback_restant} suffix="€" accent="green" />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default StatsPanel;
